import { useState, useEffect, useCallback, useMemo, memo } from "react";
import { Autocomplete, Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, MenuItem, Select, Skeleton, Stack, Switch, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { dataGridStyle } from "../../../theme";
import API from "../../../api";
import { SWAP_MODES, DATA_TYPES, NUMERIC_TYPES, resolveProtocolConfig, validateAddressFromConfig } from "../../../config/otProtocols";
import Header from "../../../components/Header";
import WarningDialog from "../../../components/WarningDialog";

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORY_COLOR = {
  "plc": "#4a9eff",
  "scada": "#ff9f4a",
  "factory-automation": "#4affb0",
  "building-automation": "#d4ff4a",
  "power-grid": "#ff4a4a",
  "utilities": "#c04aff",
  "iiot": "#4affff",
  "messaging": "#ff4acf",
  "robotics": "#ffd04a",
  "healthcare": "#ff6b6b",
  "medical": "#ff6b6b",
  "cnc": "#b4ff4a",
  "manufacturing": "#b4ff4a",
  "safety": "#ff8c4a",
  "infrastructure": "#aaaaaa",
  "time-sync": "#aaaaaa",
};
const catColor = (c) => CATEGORY_COLOR[c] ?? "#666666";


// ─── Tag helpers ──────────────────────────────────────────────────────────────

// Tags from DB with empty address are system/internal — must be hidden and preserved
const isSystemTag = (t) => t.address == null || t.address === "";

const emptyTag = (protoConfig) => ({
  enabled: true, area: (protoConfig?.areas ?? [])[0] ?? "",
  address: "", offset: "", name: "", data_type: "int16",
  swap_mode: "CDAB", unit: "", min_value: "", max_value: "",
  autodetected: false, _key: Math.random().toString(36).slice(2),
});

const emptyForm = (firstKey = "modbus") => ({ ip: "", protocol: firstKey, tags: [] });

// ─── TagRow ───────────────────────────────────────────────────────────────────

const TagRow = memo(({ tag, index, protoConfig, onUpdate, onRemove }) => {
  const areas = protoConfig?.areas ?? [];
  const showSwap = protoConfig?.supportsSwap ?? false;
  const showOffset = protoConfig?.supportsOffset ?? false;
  const isNumeric = NUMERIC_TYPES.has(tag.data_type);
  const isEnabled = tag.enabled !== false;
  const addrError = validateAddressFromConfig(protoConfig, tag.area, tag.address);

  const handleChange = useCallback((updated) => onUpdate(index, updated), [onUpdate, index]);
  const handleDelete = useCallback(() => onRemove(index), [onRemove, index]);

  const upd = (key) => (e) => {
    const patch = { [key]: e.target.value };
    if (key === "data_type") patch.autodetected = false;
    handleChange({ ...tag, ...patch });
  };
  const updNum = (key) => (e) => {
    const v = e.target.value;
    handleChange({ ...tag, [key]: v === "" ? "" : Number(v) });
  };

  return (
    <TableRow hover sx={{ opacity: isEnabled ? 1 : 0.45 }}>
      <TableCell sx={{ p: 0.5 }} align="center">
        <Tooltip
          title={isEnabled ? "Enabled — visible in OT Monitor" : "Disabled — hidden from OT Monitor"}
          placement="top"
        >
          <Switch
            size="small"
            checked={isEnabled}
            onChange={() => handleChange({ ...tag, enabled: !isEnabled })}
            color="success"
          />
        </Tooltip>
      </TableCell>

      {areas.length > 0 && (
        <TableCell sx={{ p: 0.5, minWidth: 160 }}>
          <Select
            size="small"
            value={tag.area || areas[0] || ""}
            onChange={(e) => handleChange({ ...tag, area: e.target.value })}
            sx={{ minWidth: 140, fontSize: 13 }}
          >
            {areas.map((a) => <MenuItem key={a} value={a} sx={{ fontSize: 13 }}>{a}</MenuItem>)}
          </Select>
        </TableCell>
      )}

      <TableCell sx={{ p: 0.5 }}>
        <Tooltip title={addrError ?? ""} open={!!addrError} placement="top" arrow>
          <TextField
            size="small"
            value={tag.address}
            onChange={upd("address")}
            placeholder={areas.length ? "e.g. 0" : "identifier / address"}
            error={!!addrError}
            sx={{ width: 150 }}
          />
        </Tooltip>
      </TableCell>

      {showOffset && (
        <TableCell sx={{ p: 0.5 }}>
          <TextField size="small" value={tag.offset ?? ""} onChange={upd("offset")} placeholder="0" sx={{ width: 70 }} />
        </TableCell>
      )}

      <TableCell sx={{ p: 0.5 }}>
        <TextField size="small" value={tag.name} onChange={upd("name")} placeholder="tag name" sx={{ width: 150 }} />
      </TableCell>

      <TableCell sx={{ p: 0.5, minWidth: 120 }}>
        <Select
          size="small"
          value={tag.data_type}
          onChange={(e) => handleChange({ ...tag, data_type: e.target.value, autodetected: false })}
          sx={{ minWidth: 110, fontSize: 13 }}
        >
          {DATA_TYPES.map((dt) => <MenuItem key={dt} value={dt} sx={{ fontSize: 13 }}>{dt}</MenuItem>)}
        </Select>
      </TableCell>

      {showSwap && (
        <TableCell sx={{ p: 0.5, minWidth: 100 }}>
          <Select
            size="small"
            value={tag.swap_mode}
            onChange={(e) => handleChange({ ...tag, swap_mode: e.target.value })}
            sx={{ minWidth: 90, fontSize: 13 }}
          >
            {SWAP_MODES.map((m) => <MenuItem key={m} value={m} sx={{ fontSize: 13 }}>{m}</MenuItem>)}
          </Select>
        </TableCell>
      )}

      <TableCell sx={{ p: 0.5 }}>
        <TextField size="small" value={tag.unit} onChange={upd("unit")} placeholder="°C" sx={{ width: 80 }} />
      </TableCell>

      <TableCell sx={{ p: 0.5 }}>
        {isNumeric && (
          <TextField size="small" type="number" value={tag.min_value ?? ""} onChange={updNum("min_value")} placeholder="min" sx={{ width: 90 }} />
        )}
      </TableCell>

      <TableCell sx={{ p: 0.5 }}>
        {isNumeric && (
          <TextField size="small" type="number" value={tag.max_value ?? ""} onChange={updNum("max_value")} placeholder="max" sx={{ width: 90 }} />
        )}
      </TableCell>

      <TableCell sx={{ p: 0.5 }} align="center">
        {tag.autodetected && (
          <Tooltip title="Data type auto-detected" placement="top">
            <AutoFixHighIcon fontSize="small" sx={{ color: "#4cceac", verticalAlign: "middle" }} />
          </Tooltip>
        )}
      </TableCell>

      <TableCell sx={{ p: 0.5 }} align="center">
        <Tooltip title="Remove tag">
          <IconButton size="small" color="error" onClick={handleDelete}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
});

// ─── Protocol info accordion ──────────────────────────────────────────────────

const ProtocolInfo = ({ protoConfig }) => {
  if (!protoConfig) return null;
  const { vendor, standard, default_ports, devices, categories } = protoConfig;
  if (!vendor && !standard && !default_ports?.length && !devices) return null;

  return (
    <Accordion disableGutters sx={{
      bgcolor: "#161616", border: "1px solid #2a2a2a",
      borderRadius: "4px !important", mb: 2,
      "&:before": { display: "none" },
    }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 14, color: "#666" }} />}
        sx={{ minHeight: 36, "& .MuiAccordionSummary-content": { my: 0, alignItems: "center", gap: 0.8 } }}
      >
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>Protocol info</Typography>
        {categories.slice(0, 3).map((c) => (
          <Box key={c} sx={{
            px: 0.6, py: 0.1,
            bgcolor: `${catColor(c)}22`, border: `1px solid ${catColor(c)}55`, borderRadius: 0.5,
          }}>
            <Typography sx={{ fontSize: 9, color: catColor(c) }}>{c}</Typography>
          </Box>
        ))}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1.5 }}>
        <Stack direction="row" flexWrap="wrap" gap={2.5}>
          {vendor && (
            <Box>
              <Typography sx={{ fontSize: 9, color: "text.secondary", mb: 0.3 }}>Vendor</Typography>
              <Typography sx={{ fontSize: 11, color: "#ccc" }}>{vendor}</Typography>
            </Box>
          )}
          {standard && (
            <Box>
              <Typography sx={{ fontSize: 9, color: "text.secondary", mb: 0.3 }}>Standard</Typography>
              <Typography sx={{ fontSize: 11, color: "#ccc" }}>{standard}</Typography>
            </Box>
          )}
          {default_ports?.length > 0 && (
            <Box>
              <Typography sx={{ fontSize: 9, color: "text.secondary", mb: 0.5 }}>Default ports</Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {default_ports.map((p) => (
                  <Tooltip key={p.port} title={p.group ?? ""} placement="top">
                    <Box sx={{ px: 0.7, py: 0.2, bgcolor: "#2a2a2a", border: "1px solid #3a3a3a", borderRadius: 0.5, cursor: "default" }}>
                      <Typography sx={{ fontSize: 10, fontFamily: "monospace", color: "#459095" }}>{p.port}</Typography>
                    </Box>
                  </Tooltip>
                ))}
              </Stack>
            </Box>
          )}
          {devices && (
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontSize: 9, color: "text.secondary", mb: 0.3 }}>Devices</Typography>
              <Typography sx={{ fontSize: 11, color: "#999" }}>{devices}</Typography>
            </Box>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

// ─── Filter bar ───────────────────────────────────────────────────────────────

const OtFilterBar = ({
  protocols,
  filterIp, setFilterIp,
  filterProtocols, setFilterProtocols,
  filterTagName, setFilterTagName,
  filterAddress, setFilterAddress,
}) => {
  const [open, setOpen] = useState(true);

  const activeCount =
    (filterIp ? 1 : 0) +
    filterProtocols.length +
    (filterTagName ? 1 : 0) +
    (filterAddress ? 1 : 0);

  const clearAll = () => {
    setFilterIp("");
    setFilterProtocols([]);
    setFilterTagName("");
    setFilterAddress("");
  };

  return (
    <Box sx={{ bgcolor: "#1e1e1e", border: "1px solid #2e2e2e", borderRadius: 1.5 }}>
      <Stack
        direction="row" alignItems="center" justifyContent="space-between"
        onClick={() => setOpen((v) => !v)}
        sx={{ px: 1.5, py: 1, cursor: "pointer", userSelect: "none" }}
      >
        <Stack direction="row" alignItems="center" spacing={0.8}>
          <FilterListIcon sx={{ fontSize: 15, color: "#666" }} />
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Filters</Typography>
          {activeCount > 0 && (
            <Box sx={{ px: 0.7, py: 0.1, bgcolor: "#45909530", borderRadius: 0.5 }}>
              <Typography sx={{ fontSize: 9, color: "#459095" }}>{activeCount} active</Typography>
            </Box>
          )}
        </Stack>
        <ExpandMoreIcon sx={{
          fontSize: 16, color: "#555",
          transform: open ? "rotate(180deg)" : "none",
          transition: "transform 0.2s",
        }} />
      </Stack>

      <Collapse in={open}>
        <Divider />
        <Box sx={{ px: 1.5, pt: 1.2, pb: 1.5 }}>
          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">

            <TextField
              size="small"
              label="Host / IP"
              value={filterIp}
              onChange={(e) => setFilterIp(e.target.value)}
              sx={{ flex: 1, minWidth: 160, "& input": { fontFamily: "monospace" } }}
            />

            <Autocomplete
              multiple
              size="small"
              options={protocols}
              value={filterProtocols}
              onChange={(_, v) => setFilterProtocols(v)}
              getOptionLabel={(p) => p.label}
              isOptionEqualToValue={(a, b) => a.key === b.key}
              limitTags={2}
              sx={{ flex: 2, minWidth: 220 }}
              renderInput={(params) => <TextField {...params} label="Protocol" />}
              slotProps={{
                paper: {
                  sx: { bgcolor: "#1e1e1e", border: "1px solid #333", maxHeight: 320 },
                },
              }}
            />

            <TextField
              size="small"
              label="Tag name"
              value={filterTagName}
              onChange={(e) => setFilterTagName(e.target.value)}
              sx={{ flex: 1, minWidth: 140 }}
            />

            <TextField
              size="small"
              label="Address"
              value={filterAddress}
              onChange={(e) => setFilterAddress(e.target.value)}
              sx={{ flex: 1, minWidth: 120, "& input": { fontFamily: "monospace" } }}
            />

            {activeCount > 0 && (
              <Button
                size="small"
                onClick={clearAll}
                sx={{ fontSize: 11, textTransform: "none", color: "#777", flexShrink: 0 }}
              >
                Clear all
              </Button>
            )}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const OtConfiguration = () => {
  const [protocols, setProtocols] = useState([]);
  const [protoLoading, setProtoLoading] = useState(true);
  const [protoError, setProtoError] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [fetchSeq, setFetchSeq] = useState(0);
  const [backendQuery, setBackendQuery] = useState({ page: 0, pageSize: 50, ip: "", protocols: [], tagName: "", address: "" });
  const paginationModel = useMemo(() => ({ page: backendQuery.page, pageSize: backendQuery.pageSize }), [backendQuery]);
  const onPaginationModelChange = useCallback((m) => setBackendQuery((q) => ({ ...q, page: m.page, pageSize: m.pageSize })), []);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [systemTags, setSystemTags] = useState([]);
  const [tagCache, setTagCache] = useState({});   // { pageNum: [{...tag, _key, _origIdx}] }
  const [tagTotalCount, setTagTotalCount] = useState(0);
  const [tagPageLoading, setTagPageLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [tagSearch, setTagSearch] = useState("");
  const [tagPage, setTagPage] = useState(0);
  const TAG_PAGE_SIZE = 25;

  // ── Filter state ──
  const [filterIp, setFilterIp] = useState("");
  const [filterProtocols, setFilterProtocols] = useState([]);
  const [filterTagName, setFilterTagName] = useState("");
  const [filterAddress, setFilterAddress] = useState("");

  // ── Delete confirmation state (WarningDialog) ──
  const [pendingDeleteConfig, setPendingDeleteConfig] = useState(null);
  const [openDeleteConfigConfirm, setOpenDeleteConfigConfirm] = useState(false);
  const [pendingDeleteTagIdx, setPendingDeleteTagIdx] = useState(null);
  const [openDeleteTagConfirm, setOpenDeleteTagConfirm] = useState(false);

  const currentProto = useMemo(
    () => protocols.find((p) => p.key === form.protocol) ?? null,
    [protocols, form.protocol],
  );

  const filteredTagList = useMemo(() => {
    const page = tagCache[tagPage] ?? [];
    const nonDeleted = page.map((t, i) => ({ t, i })).filter(({ t }) => !t._deleted);
    // In add mode (no editingId), apply client-side text filter since there's no backend data
    if (!editingId && tagSearch) {
      const q = tagSearch.toLowerCase();
      return nonDeleted.filter(({ t }) =>
        t.name?.toLowerCase().includes(q) ||
        String(t.address ?? "").toLowerCase().includes(q)
      );
    }
    return nonDeleted;
  }, [tagCache, tagPage, tagSearch, editingId]);

  // ── Load supported protocols from DB ──
  const fetchProtocols = useCallback(async () => {
    setProtoLoading(true);
    setProtoError(null);
    try {
      const res = await API.getSystemConfig("ot", "supported_protocols");
      const list = res.data?.[0]?.value ?? [];
      setProtocols(list.map(resolveProtocolConfig).sort((a, b) => a.label?.localeCompare(b.label)));
    } catch (err) {
      console.error("[OtConfiguration] fetchProtocols failed:", err?.response ?? err);
      setProtoError(`Cannot load protocols: ${err?.response?.status ?? err?.message ?? "network error"}`);
      setProtocols([]);
    } finally {
      setProtoLoading(false);
    }
  }, []);

  // ── Fetch device configs ──
  // ── Sync text filters → backendQuery (debounced 400ms, reset page)
  useEffect(() => {
    const id = setTimeout(() => {
      setBackendQuery((q) => ({ ...q, page: 0, ip: filterIp, tagName: filterTagName, address: filterAddress }));
    }, 400);
    return () => clearTimeout(id);
  }, [filterIp, filterTagName, filterAddress]);

  // ── Sync protocol filter → backendQuery (immediate, reset page)
  useEffect(() => {
    setBackendQuery((q) => ({ ...q, page: 0, protocols: filterProtocols.map((p) => p.key) }));
  }, [filterProtocols]);

  // ── Fetch whenever backendQuery or fetchSeq changes ──
  useEffect(() => {
    let cancelled = false;
    const { page, pageSize, ip, protocols, tagName, address } = backendQuery;
    const params = { page, pageSize };
    if (ip) params.ip = ip;
    if (protocols.length) params.protocol = protocols.join(',');
    if (tagName) params.tagName = tagName;
    if (address) params.address = address;
    setLoading(true);
    API.getOtConfigs(params)
      .then((res) => {
        if (cancelled) return;
        setRows((res.data?.data ?? []).map((d) => ({ ...d, id: d._id })));
        setRowCount(res.data?.total ?? 0);
      })
      .catch(() => { if (!cancelled) setError("Failed to load configurations."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [backendQuery, fetchSeq]);

  useEffect(() => { fetchProtocols(); }, [fetchProtocols]);

  // ── Load a single tag page from backend ──
  const loadTagPage = useCallback(async (ip, protocol, page, search = "") => {
    setTagPageLoading(true);
    try {
      const body = { ip, protocol, page, pageSize: TAG_PAGE_SIZE };
      if (search) body.search = search;
      const res = await API.getOtConfigTags(body);
      const { tags, total, systemTags: sysTags } = res.data;
      setTagCache((c) => ({
        ...c,
        // _origIdx comes from backend — correct even when search is active
        [page]: tags.map((t) => ({ ...t, _key: Math.random().toString(36).slice(2) })),
      }));
      setTagTotalCount(total);
      if (page === 0 && sysTags) setSystemTags(sysTags);
    } catch {
      setError("Failed to load tags.");
    } finally {
      setTagPageLoading(false);
    }
  }, [TAG_PAGE_SIZE]);

  // ── Debounce tag search → clear cache and reload from backend ──
  useEffect(() => {
    if (!editingId) return;
    const id = setTimeout(() => {
      setTagCache({});
      setTagPage(0);
      loadTagPage(form.ip, form.protocol, 0, tagSearch);
    }, 400);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagSearch]);

  // ── Dialog helpers ──
  const openAdd = () => {
    setEditingId(null);
    setSystemTags([]);
    setTagCache({});
    setTagTotalCount(0);
    setTagSearch("");
    setTagPage(0);
    setForm(emptyForm(protocols[0]?.key ?? "modbus"));
    setError(null);
    setDialog(true);
  };

  const openEdit = useCallback((row) => {
    setEditingId(true);
    setTagCache({});
    setTagTotalCount(0);
    setSystemTags([]);
    setTagSearch("");
    setTagPage(0);
    setForm({ ip: row.ip ?? "", protocol: row.protocol ?? protocols[0]?.key ?? "modbus" });
    setError(null);
    setDialog(true);
    loadTagPage(row.ip, row.protocol, 0);
  }, [protocols, loadTagPage]);

  const handleDelete = useCallback((row) => {
    setPendingDeleteConfig(row);
    setOpenDeleteConfigConfirm(true);
  }, []);

  const confirmDeleteConfig = useCallback(async (row) => {
    try {
      await API.deleteOtConfig(row.ip, row.protocol);
      setFetchSeq((n) => n + 1);
    } catch {
      alert("Delete failed.");
    }
  }, []);

  // ── Tag helpers ──
  const addTag = () => {
    const tag = emptyTag(currentProto);
    setTagCache((c) => ({ ...c, [tagPage]: [...(c[tagPage] ?? []), tag] }));
    setTagTotalCount((n) => n + 1);
  };

  const updateTag = useCallback((idx, updated) =>
    setTagCache((c) => {
      const page = [...(c[tagPage] ?? [])];
      page[idx] = updated;
      return { ...c, [tagPage]: page };
    }), [tagPage]);

  const deleteTag = useCallback((idx) => {
    setTagCache((c) => {
      const page = [...(c[tagPage] ?? [])];
      if (page[idx]?._origIdx !== undefined) {
        page[idx] = { ...page[idx], _deleted: true };
      } else {
        page.splice(idx, 1);
      }
      return { ...c, [tagPage]: page };
    });
    setTagTotalCount((n) => Math.max(0, n - 1));
  }, [tagPage]);

  const handleTagPageChange = async (_, newPage) => {
    setTagPage(newPage);
    if (!tagCache[newPage]) await loadTagPage(form.ip, form.protocol, newPage, tagSearch);
  };

  const handleProtocolChange = (newKey) => {
    const newProto = protocols.find((p) => p.key === newKey);
    setForm((f) => ({ ...f, protocol: newKey }));
    setTagCache((c) => {
      const updated = {};
      for (const [p, tags] of Object.entries(c)) {
        updated[p] = tags.map((t) => ({ ...t, area: (newProto?.areas ?? [])[0] ?? "" }));
      }
      return updated;
    });
  };

  // ── Save ──
  const handleSave = async () => {
    if (!form.ip.trim()) { setError("IP / Host is required."); return; }
    if (!form.protocol) { setError("Protocol is required."); return; }

    // Validate loaded pages
    for (const pageTags of Object.values(tagCache)) {
      const bad = pageTags.find(
        (t) => !t._deleted && t.address && validateAddressFromConfig(currentProto, t.area, t.address),
      );
      if (bad) { setError(`Invalid address "${bad.address}" for ${form.protocol}.`); return; }
    }

    setSaving(true);
    setError(null);
    try {
      let finalVisible;

      if (editingId) {
        // Fetch current server state to get unloaded pages intact
        const serverRes = await API.getOtConfig(form.ip.trim(), form.protocol);
        const serverVisible = (serverRes.data?.data?.tags ?? []).filter((t) => !isSystemTag(t));

        // Apply local cache changes by _origIdx
        const result = [...serverVisible];
        const deletedSet = new Set();
        const newTags = [];

        for (const pageTags of Object.values(tagCache)) {
          for (const tag of pageTags) {
            if (tag._origIdx !== undefined) {
              if (tag._deleted) {
                deletedSet.add(tag._origIdx);
              } else {
                const { _key, _origIdx, _deleted, ...clean } = tag;
                result[_origIdx] = clean;
              }
            } else if (!tag._deleted) {
              const { _key, _deleted, ...clean } = tag;
              newTags.push(clean);
            }
          }
        }
        finalVisible = result.filter((_, i) => !deletedSet.has(i)).concat(newTags);
      } else {
        // New config — collect all tags from cache in page order
        finalVisible = Object.keys(tagCache)
          .sort((a, b) => Number(a) - Number(b))
          .flatMap((p) => tagCache[p].filter((t) => !t._deleted).map(({ _key, _deleted, _origIdx, ...rest }) => rest));
      }

      const payload = {
        ip: form.ip.trim(),
        protocol: form.protocol,
        tags: [...systemTags, ...finalVisible],
      };

      if (editingId) await API.updateOtConfig(form.ip.trim(), form.protocol, payload);
      else await API.createOtConfig(payload);
      setDialog(false);
      setFetchSeq((n) => n + 1);
    } catch {
      setError("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const activeFilterCount = (filterIp ? 1 : 0) + filterProtocols.length + (filterTagName ? 1 : 0) + (filterAddress ? 1 : 0);

  // ── DataGrid columns ──
  const columns = useMemo(() => [
    {
      field: "ip",
      headerName: "IP / HOST",
      minWidth: 160,
      renderCell: (p) => (
        <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "#459095" }}>
          {p.value}
        </Typography>
      ),
    },
    {
      field: "protocol",
      headerName: "PROTOCOL",
      minWidth: 200,
      valueGetter: (value, row) => {
        const proto = protocols.find((pr) => pr.key === row.protocol);
        return proto?.label ?? row.protocol ?? "";
      },
      renderCell: (p) => (
        <Chip
          label={p.value}
          size="small"
          sx={{ fontSize: 10, fontWeight: 600, maxWidth: 190, bgcolor: "#2a2a2a", border: "1px solid #3a3a3a", color: "#ccc" }}
        />
      ),
    },
    {
      field: "tags",
      headerName: "TAGS",
      width: 72,
      align: "center",
      headerAlign: "center",
      valueGetter: (value, row) => (row.tags ?? []).filter((t) => !isSystemTag(t)).length,
      renderCell: (p) => (
        <Box sx={{ px: 0.8, py: 0.2, bgcolor: "#2a2a2a", border: "1px solid #333", borderRadius: 0.8 }}>
          <Typography sx={{ fontSize: 10, color: "text.secondary", fontFamily: "monospace" }}>
            {p.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "_tag_names",
      headerName: "TAG NAMES",
      flex: 1,
      valueGetter: (value, row) =>
        (row.tags ?? []).filter((t) => !isSystemTag(t)).map((t) => t.name).filter(Boolean).sort((a, b) => a.localeCompare(b)).join(", ") || "",
      renderCell: (p) => {
        const names = (p.row.tags ?? []).filter((t) => !isSystemTag(t)).map((t) => t.name).filter(Boolean).sort((a, b) => a.localeCompare(b));
        if (names.length === 0) return <Typography sx={{ fontSize: 11, color: "#555" }}>—</Typography>;
        const MAX = 3;
        const visible = names.slice(0, MAX);
        const rest = names.length - MAX;
        return (
          <Stack direction="row" flexWrap="wrap" gap={0.4} sx={{ py: 0.5 }}>
            {visible.map((name, i) => (
              <Box key={i} sx={{ px: 0.6, py: 0.1, bgcolor: "#252525", border: "1px solid #383838", borderRadius: 0.4 }}>
                <Typography sx={{ fontSize: 10, color: "#bbb", fontFamily: "monospace" }}>{name}</Typography>
              </Box>
            ))}
            {rest > 0 && (
              <Box sx={{ px: 0.6, py: 0.1, bgcolor: "#45909520", border: "1px solid #45909550", borderRadius: 0.4 }}>
                <Typography sx={{ fontSize: 10, color: "#459095" }}>+{rest}</Typography>
              </Box>
            )}
          </Stack>
        );
      },
    },
    {
      field: "_actions",
      headerName: "",
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Stack direction="row" gap={0.5}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => openEdit(p.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(p.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ], [protocols, openEdit, handleDelete]);

  const showAreas = (currentProto?.areas?.length ?? 0) > 0;
  const showSwap = currentProto?.supportsSwap ?? false;
  const showOffset = currentProto?.supportsOffset ?? false;
  const tagColSpan = 9 + (showAreas ? 1 : 0) + (showSwap ? 1 : 0) + (showOffset ? 1 : 0);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>

      {/* ── Header (fix 1: h2 bold come Header standard) ── */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Header
            sx={{ width: { xs: "40vw", md: "" } }}
            title="OT Tag Configuration"
            subtitle=""
            desc="Define tags (signals) to monitor per device — maps IP + protocol to register list."
          />
          {/* <Typography variant="h2" fontWeight="bold" color="grey.100">
            OT Tag Configuration
          </Typography>
          <Typography variant="h5" sx={{ color: "greenAccent.400", mt: 0.3 }}>
            Define tags (signals) to monitor per device — maps IP + protocol to register list.
          </Typography> */}
        </Box>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={openAdd}
          disabled={protoLoading || protocols.length === 0}
          sx={{
            mt: 1, flexShrink: 0, fontSize: 12, textTransform: "none",
            borderColor: "#459095", color: "#459095",
            "&:hover": { borderColor: "#459095", bgcolor: "#45909515" },
          }}
        >
          Add Configuration
        </Button>
      </Stack>

      {/* ── Stats ── */}
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
          {activeFilterCount > 0
            ? `${rowCount} matching device${rowCount !== 1 ? "s" : ""}`
            : `${rowCount} device${rowCount !== 1 ? "s" : ""} configured`}
        </Typography>
        <Typography sx={{ fontSize: 11, color: "#444" }}>·</Typography>
        {protoError ? (
          <Typography sx={{ fontSize: 11, color: "#e57373" }}>{protoError}</Typography>
        ) : (
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
            {protoLoading ? "loading protocols…" : `${protocols.length} protocols available`}
          </Typography>
        )}
      </Stack>

      {/* ── Filter bar (fix 4) ── */}
      <OtFilterBar
        protocols={protocols}
        filterIp={filterIp} setFilterIp={setFilterIp}
        filterProtocols={filterProtocols} setFilterProtocols={setFilterProtocols}
        filterTagName={filterTagName} setFilterTagName={setFilterTagName}
        filterAddress={filterAddress} setFilterAddress={setFilterAddress}
      />

      {/* ── DataGrid ── */}
      <Box sx={{
        bgcolor: "#1e1e1e", border: "1px solid #2e2e2e",
        borderRadius: 1.5, overflow: "hidden", height: "60vh",
      }}>
        <DataGridPro
          sx={dataGridStyle}
          rows={rows}
          rowCount={rowCount}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={[25, 50, 100]}
          columns={columns}
          loading={loading}
          getRowId={(r) => r._id}
          getRowHeight={() => "auto"}
          slotProps={{ loadingOverlay: { variant: "skeleton" } }}
        />
      </Box>

      {/* ── Add / Edit dialog ── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: "60vh", bgcolor: "#1a1a1a", border: "1px solid #333", backgroundImage: "none" },
        }}
      >
        <DialogTitle sx={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          pb: 1, bgcolor: "#1e1e1e", borderBottom: "1px solid #2e2e2e",
        }}>
          <Typography variant="h6" fontWeight={700}>
            {editingId ? "Edit Configuration" : "New Configuration"}
          </Typography>
          <IconButton size="small" onClick={() => setDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: "#2e2e2e", bgcolor: "#1a1a1a" }}>

          {/* ── Device fields — fix 2: read-only in edit mode ── */}
          {editingId ? (
            <Stack direction="row" alignItems="center" gap={3} sx={{ mb: 2.5, flexWrap: "wrap" }}>
              <Box>
                <Typography sx={{ fontSize: 10, color: "text.secondary", mb: 0.4 }}>IP / Host</Typography>
                <Typography sx={{ fontFamily: "monospace", fontSize: 15, color: "#459095", fontWeight: 700 }}>
                  {form.ip}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, color: "text.secondary", mb: 0.4 }}>Protocol</Typography>
                <Chip
                  label={currentProto?.label ?? form.protocol}
                  size="small"
                  sx={{ fontSize: 11, fontWeight: 600, bgcolor: "#2a2a2a", border: "1px solid #3a3a3a", color: "#ccc" }}
                />
              </Box>
              <Typography sx={{ fontSize: 10, color: "text.disabled", alignSelf: "flex-end", pb: 0.3 }}>
                IP and protocol cannot be changed after creation
              </Typography>
            </Stack>
          ) : (
            <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
              <TextField
                label="IP / Host"
                size="small"
                value={form.ip}
                onChange={(e) => setForm((f) => ({ ...f, ip: e.target.value }))}
                placeholder="192.168.1.100"
                sx={{ width: 220 }}
                required
              />
              {/* fix 5: searchable Autocomplete instead of plain Select */}
              <Autocomplete
                size="small"
                options={protocols}
                value={protocols.find((p) => p.key === form.protocol) ?? null}
                onChange={(_, val) => val && handleProtocolChange(val.key)}
                getOptionLabel={(p) => p.label}
                isOptionEqualToValue={(a, b) => a.key === b.key}
                disableClearable
                sx={{ width: 320 }}
                renderInput={(params) => <TextField {...params} label="Protocol" required />}
                slotProps={{
                  paper: {
                    sx: { bgcolor: "#1e1e1e", border: "1px solid #333", maxHeight: 360 },
                  },
                }}
              />
            </Box>
          )}

          {/* ── Protocol metadata ── */}
          <ProtocolInfo protoConfig={currentProto} />

          {/* ── Tags section ── */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.8}>
              <Typography variant="subtitle2" fontWeight={700}>Tags</Typography>
              <Box sx={{ px: 0.7, py: 0.1, bgcolor: "#2a2a2a", border: "1px solid #333", borderRadius: 0.5 }}>
                <Typography sx={{ fontSize: 10, color: "text.secondary", fontFamily: "monospace" }}>
                  {tagTotalCount}
                </Typography>
              </Box>
              {systemTags.length > 0 && (
                <Tooltip title={`${systemTags.length} system tag${systemTags.length > 1 ? "s" : ""} are hidden (managed internally)`} placement="right">
                  <Box sx={{ px: 0.7, py: 0.1, bgcolor: "#33290a", border: "1px solid #4d3d0f", borderRadius: 0.5, cursor: "default" }}>
                    <Typography sx={{ fontSize: 9, color: "#b8860b" }}>
                      +{systemTags.length} system
                    </Typography>
                  </Box>
                </Tooltip>
              )}
            </Stack>
            <Stack direction="row" alignItems="center" gap={1}>
              <TextField
                size="small"
                placeholder="Search name or address…"
                value={tagSearch}
                onChange={(e) => setTagSearch(e.target.value)}
                sx={{ width: 220, "& input": { fontSize: 12 } }}
              />
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addTag}
                sx={{ fontSize: 11, textTransform: "none", color: "#459095", flexShrink: 0 }}
              >
                Add Tag
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ maxHeight: 400, overflowY: "auto", border: "1px solid #2e2e2e", borderRadius: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width={60} align="center">On</TableCell>
                  {showAreas && <TableCell>Area</TableCell>}
                  <TableCell>Address</TableCell>
                  {showOffset && <TableCell>Offset</TableCell>}
                  <TableCell>Name</TableCell>
                  <TableCell>Data type</TableCell>
                  {showSwap && <TableCell>Byte order</TableCell>}
                  <TableCell>Unit</TableCell>
                  <TableCell>Min</TableCell>
                  <TableCell>Max</TableCell>
                  <TableCell width={36} align="center" />
                  <TableCell width={48} />
                </TableRow>
              </TableHead>
              <TableBody>
                {tagPageLoading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      {Array.from({ length: tagColSpan }).map((_, j) => (
                        <TableCell key={j} sx={{ py: 0.9 }}>
                          <Skeleton variant="text" sx={{ fontSize: 13 }} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : tagTotalCount === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tagColSpan} align="center" sx={{ color: "text.secondary", py: 3 }}>
                      No tags defined — click "Add Tag" to start
                    </TableCell>
                  </TableRow>
                ) : filteredTagList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tagColSpan} align="center" sx={{ color: "text.secondary", py: 2 }}>
                      No tags match "{tagSearch}"
                    </TableCell>
                  </TableRow>
                ) : filteredTagList.map(({ t: tag, i: idx }) => (
                  <TagRow
                    key={tag._key}
                    tag={tag}
                    index={idx}
                    protoConfig={currentProto}
                    onUpdate={updateTag}
                    onRemove={(i) => { setPendingDeleteTagIdx(i); setOpenDeleteTagConfirm(true); }}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
          {tagTotalCount > TAG_PAGE_SIZE && (
            <TablePagination
              component="div"
              count={tagTotalCount}
              page={tagPage}
              onPageChange={handleTagPageChange}
              rowsPerPage={TAG_PAGE_SIZE}
              rowsPerPageOptions={[TAG_PAGE_SIZE]}
              sx={{ borderTop: "1px solid #2e2e2e", "& .MuiTablePagination-toolbar": { minHeight: 40 }, "& .MuiTablePagination-displayedRows": { fontSize: 11 } }}
            />
          )}

          {error && (
            <Typography color="error" variant="caption" sx={{ mt: 1, display: "block" }}>
              {error}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, bgcolor: "#1e1e1e", borderTop: "1px solid #2e2e2e" }}>
          <Button
            onClick={() => setDialog(false)}
            sx={{ textTransform: "none", color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ textTransform: "none", bgcolor: "#459095", "&:hover": { bgcolor: "#3a7d80" } }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <WarningDialog
        open={openDeleteConfigConfirm}
        setOpen={setOpenDeleteConfigConfirm}
        setCheck={(val) => {
          setOpenDeleteConfigConfirm(false);
          if (val && pendingDeleteConfig) { confirmDeleteConfig(pendingDeleteConfig); setPendingDeleteConfig(null); }
        }}
        message={`This will permanently delete the configuration for ${pendingDeleteConfig?.ip ?? "this device"}${pendingDeleteConfig?.protocol ? ` (${pendingDeleteConfig.protocol})` : ""} and all its tags.`}
      />

      <WarningDialog
        open={openDeleteTagConfirm}
        setOpen={setOpenDeleteTagConfirm}
        setCheck={(val) => {
          setOpenDeleteTagConfirm(false);
          if (val && pendingDeleteTagIdx !== null) { deleteTag(pendingDeleteTagIdx); setPendingDeleteTagIdx(null); }
        }}
        message="This tag will be removed from the configuration."
      />
    </Box>
  );
};

export default OtConfiguration;
